import React, { useEffect } from "react";
import { motion } from "framer-motion";
import Header from "./components/Header";
import Section from "./components/Section";
import Card from "./components/Card";
import Sticky from "./components/Sticky";
import Timeline from "./components/Timeline";
import Footer from "./components/Footer";
import experiences from "./data/experiences.json";

// One-time: ensure print rules + reduced motion are applied
export default function App() {
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @media (prefers-reduced-motion: reduce) {
        * { animation: none !important; transition: none !important; }
      }
      .print-show { display: none; }
      .printing * { animation-duration: 0s !important; transition-duration: 0s !important; }
      @media print {
        .print-hide { display: none !important; }
        .print-show { display: inline !important; }
      }
      @media print {
        @page { margin: 1in; size: letter; }

        /* ATS Rule: Simple font, reset all decoration */
        *, *::before, *::after {
          font-family: Arial, Calibri, Helvetica, sans-serif !important;
          animation: none !important;
          transition: none !important;
          transform: none !important;
          opacity: 1 !important;
          visibility: visible !important;
          box-shadow: none !important;
          --tw-ring-shadow: none !important;
          --tw-shadow: none !important;
          text-shadow: none !important;
          border-radius: 0 !important;
          background-image: none !important;
        }

        /* ATS Rule: White background, black text */
        body, main, html, div, section, aside, article, header, footer, ol, ul, li, p, span, h1, h2, h3 {
          background: #fff !important;
          color: #000 !important;
          margin-left: 0 !important;
          margin-right: 0 !important;
        }

        /* Hide non-content elements — high specificity to override flex-wrap rules */
        .no-print, .no-export, img[alt="Profile"], footer,
        header .no-print, button.no-print,
        header .flex-wrap > .no-print { display: none !important; }

        /* ATS Rule: Single column, no grid */
        .max-w-5xl { max-width: 100% !important; padding: 0 !important; margin: 0 !important; }
        .py-10 { padding: 0 !important; }
        .grid { display: block !important; }
        .lg\\:col-span-8, .lg\\:col-span-4 { width: 100% !important; }
        .sticky, .lg\\:sticky { position: static !important; }
        .gap-8, .gap-6 { gap: 0 !important; }

        /* Hide all decorative color bars */
        .h-2 { display: none !important; }

        /* ===== CONTACT HEADER ===== */
        header {
          border: none !important;
          padding: 0 !important;
          margin-bottom: 6pt !important;
          overflow: visible !important;
        }
        header .px-6 { padding: 0 !important; }
        header .flex { display: block !important; }
        header h1 {
          font-size: 18pt !important;
          font-weight: bold !important;
          margin-bottom: 2pt !important;
        }
        header p {
          font-size: 10pt !important;
          margin: 0 !important;
        }
        header .mt-4 { margin-top: 4pt !important; }
        header .flex-wrap {
          display: block !important;
        }
        header .flex-wrap > *:not(.no-print) {
          display: inline !important;
          font-size: 10pt !important;
        }
        header .flex-wrap > *:not(.no-print)::after {
          content: " | " !important;
        }
        header .flex-wrap > *:not(.no-print):last-of-type::after,
        header .flex-wrap > *:nth-last-child(2)::after {
          content: "" !important;
        }
        header a {
          color: #000 !important;
          text-decoration: none !important;
        }

        /* ===== SECTION HEADINGS (ATS: standard clear headings) ===== */
        .bg-\\[\\#A7C7E7\\] {
          background: #fff !important;
          padding: 0 !important;
          margin: 0 !important;
          border-bottom: 1.5pt solid #000 !important;
        }
        .-mx-6 { margin: 0 !important; }
        .bg-\\[\\#A7C7E7\\] h2,
        h2 {
          color: #000 !important;
          font-size: 12pt !important;
          font-weight: bold !important;
          text-transform: uppercase !important;
          padding: 0 !important;
          margin: 0 0 4pt 0 !important;
        }
        .mt-10 { margin-top: 10pt !important; }
        .mt-4 { margin-top: 4pt !important; }

        /* ===== TIMELINE: strip all visual styling ===== */
        ol.border-l {
          border: none !important;
          margin: 0 !important;
          padding: 0 !important;
          list-style: none !important;
        }
        ol li {
          padding: 0 0 8pt 0 !important;
          margin: 0 !important;
        }
        /* Hide timeline dots */
        ol li > span.rounded-full { display: none !important; }
        /* Hide date badges (date is shown in the article header) */
        ol li > div > span.rounded { display: none !important; }

        /* ===== EXPERIENCE ARTICLES: plain text ===== */
        article {
          border: none !important;
          margin: 0 !important;
          padding: 0 !important;
          overflow: visible !important;
        }
        article .bg-\\[\\#E5E7EB\\] {
          background: #fff !important;
          padding: 0 !important;
        }
        article h3 {
          font-size: 11pt !important;
          font-weight: bold !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        article .p-5 { padding: 2pt 0 0 0 !important; }
        /* Location badges: inline plain text */
        article .mb-3 { margin-bottom: 2pt !important; }
        article .mb-3 span {
          background: none !important;
          border: none !important;
          padding: 0 !important;
          font-size: 10pt !important;
          font-style: italic !important;
        }
        /* Bullet points: ATS-friendly list */
        article ul {
          padding-left: 18pt !important;
          margin: 2pt 0 0 0 !important;
        }
        article li {
          font-size: 10pt !important;
          line-height: 1.4 !important;
          padding: 0 !important;
          margin: 1pt 0 !important;
        }

        /* ===== DATE BADGES in timeline ===== */
        ol li > .mb-2 {
          margin: 0 !important;
          padding: 0 !important;
        }
        ol li > .mb-2 span {
          background: none !important;
          border: none !important;
          padding: 0 !important;
          font-size: 10pt !important;
          font-weight: normal !important;
          font-style: italic !important;
        }

        /* ===== SIDEBAR SECTIONS (Skills, Education, Certs, Languages) ===== */
        .space-y-8 > * + * { margin-top: 6pt !important; }
        /* Card wrapper: strip */
        .p-5 { padding: 2pt 0 !important; }
        /* Competencies as comma-separated or simple list */
        .grid.grid-cols-1 { display: block !important; }
        .text-\\[14\\.5px\\], .text-\\[15px\\] { font-size: 10pt !important; }
        .text-\\[16px\\] { font-size: 10pt !important; }
        .space-y-4 > * + * { margin-top: 4pt !important; }
        .space-y-2 > * + * { margin-top: 2pt !important; }
        .space-y-1 > * + * { margin-top: 1pt !important; }
        /* Education links: plain text */
        a { color: #000 !important; text-decoration: none !important; }
        .hover\\:bg-sky-50 { background: none !important; }

        /* ===== PAGE BREAK CONTROL ===== */
        article { break-inside: avoid; }
        header { break-inside: avoid; break-after: avoid; }
        section { break-inside: auto; }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const data = {
    name: "Steven Li",
    headline: "Security Engineering Leader | Product Security | Cloud & AI Security | Veteran",
    location: "Middletown, DE, USA",
    email: "stevenli6186@gmail.com",
    phone: "347-924-0625",
    linkedin: "https://www.linkedin.com/in/stevenli6186/",
    languages: ["English (Fluent)", "Chinese (Fluent)"],
    summary:
      "Security Engineering Leader with 15+ years’ experience building and leading high-performing technical teams, scaling product security maturity, and embedding secure-by-design practices in complex, high-impact environments. Proven record aligning vulnerability management, offensive security, and engineering to protect against known threats. Skilled in designing and executing security assurance programs, developing automation for continuous validation, and leading cross-functional initiatives that balance risk reduction with product velocity. Hands-on builder of AI-powered security tooling and agents, with experience establishing AI security review processes aligned to OWASP LLM Top 10 and emerging AI threat frameworks.",
    coreCompetencies: [
      "Security Program Leadership",
      "Team Building & Coaching",
      "Secure SDLC & Shift-Left DevSecOps",
      "Threat Modeling (Microsoft TM, SD Elements)",
      "Security Automation",
      "SAST/DAST (Checkmarx, Coverity)",
      "SCA & Supply Chain (Snyk, Black Duck)",
      "Vulnerability Management (Rapid7, Nessus, Qualys)",
      "CSPM (Wiz, Prisma Cloud, AWS Native)",
      "Container Security (StackRox, Prisma Cloud)",
      "Offensive Security Collaboration",
      "Zero Trust Architecture",
      "IAM",
      "Cross-Functional Program Management",
      "Cloud Security (AWS Native, SaaS)",
      "AI/GenAI Security (OWASP LLM Top 10)",
      "AI Agent & Application Development",
      "Compliance & Audit (HIPAA, FDA, ISO, NIST, SOC 2)",
      "Terraform",
      "Docker",
      "GitHub Actions",
    ],
    education: [
      {
        school: "UC Berkeley",
        degree: "MICS – Master of Information and Cybersecurity",
        period: "2020 – 2021",
        url: "https://www.berkeley.edu/",
      },
      {
        school: "St. John’s University",
        degree: "B.S., Computer Systems Networking & Telecommunications",
        period: "2009 – 2013",
        url: "https://www.stjohns.edu/",
      },
    ],
    certifications: [
      { name: "Software Security Practitioner – Architect", year: "2021", org: { name: "Security Compass", url: "https://securitycompass.com/" } },
      { name: "Nexpose Certified Administrator", org: { name: "Rapid7 Academy", url: "https://academy.rapid7.com/" } },
      { name: "Metasploit Pro Certified Specialist", org: { name: "Rapid7 Academy", url: "https://academy.rapid7.com/" } },
    ],
  };

  return (
    <main className="min-h-screen bg-gradient-to-b from-[#ECF3FF] to-[#EAF2FB] text-slate-700 dark:from-[#0B1220] dark:to-[#0B1020] dark:text-slate-200">
      <div className="max-w-5xl mx-auto px-6 py-10 print:px-0">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <Header data={data} />
        </motion.div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <motion.section className="lg:col-span-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
            <Section title="Summary">
              <Card>
                <p className="text-[16px] leading-7">{data.summary}</p>
              </Card>
            </Section>

            <Section title={`Professional Experience (${experiences.length})`}>
              <Timeline roles={experiences} />
            </Section>
          </motion.section>

          <motion.aside className="lg:col-span-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
            <Sticky>
              <Section title="Core Competencies">
                <Card>
                  <ul className="grid grid-cols-1 gap-1 text-[14.5px]">
                    {data.coreCompetencies.map((c, i) => (
                      <li key={i} className="hover:text-[#1E3A8A] transition-colors">• {c}</li>
                    ))}
                  </ul>
                </Card>
              </Section>

              <Section title="Education">
                <Card>
                  <ul className="space-y-4 text-[14.5px]">
                    {data.education.map((e, i) => (
                      <li key={i} className="hover:bg-sky-50 dark:hover:bg-slate-800 rounded p-1 transition-colors">
                        <p className="font-medium text-slate-900 dark:text-slate-100">{e.degree}</p>
                        <p>
                          {e.url ? (
                            <a href={e.url} target="_blank" rel="noreferrer" className="underline text-[#1E3A8A] dark:text-[#A7C7E7] hover:text-[#2563EB] dark:hover:text-sky-300">
                              {e.school}
                            </a>
                          ) : e.school}
                        </p>
                        <p className="opacity-70">{e.period}</p>
                      </li>
                    ))}
                  </ul>
                </Card>
              </Section>

              <Section title="Certifications">
                <Card>
                  <ul className="space-y-2 text-[14.5px]">
                    {data.certifications.map((c, i) => (
                      <li key={i} className="hover:text-[#1E3A8A] dark:hover:text-sky-300 transition-colors">
                        {c.name}
                        {c.org ? (
                          <> — {c.org.url ? (
                            <a href={c.org.url} target="_blank" rel="noreferrer" className="underline text-[#1E3A8A] dark:text-[#A7C7E7] hover:text-[#2563EB] dark:hover:text-sky-300">{c.org.name}</a>
                          ) : (
                            c.org.name || c.org
                          )}</>
                        ) : null}
                        {c.year ? ` (${c.year})` : ""}
                      </li>
                    ))}
                  </ul>
                </Card>
              </Section>

              <Section title="Languages">
                <Card>
                  <ul className="space-y-1 text-[14.5px]">
                    {data.languages.map((l, i) => (
                      <li key={i} className="hover:text-[#1E3A8A] transition-colors">• {l}</li>
                    ))}
                  </ul>
                </Card>
              </Section>
            </Sticky>
          </motion.aside>
        </div>

        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}>
          <Footer data={data} />
        </motion.div>
      </div>
    </main>
  );
}
